/**
 * Fullscreen with the vendor fallbacks that still matter.
 *
 * The important caveat is iPhone Safari: it has never supported the Fullscreen
 * API on arbitrary elements (iPad has since iPadOS 12), so `supported()` is
 * false there and the UI hides the control rather than offering a button that
 * silently does nothing. Home-screen install is the fullscreen path on iPhone,
 * which is why the page carries the apple-mobile-web-app meta tags.
 */

const el = () => document.documentElement;

const raw = {
  request: (node) =>
    node.requestFullscreen?.({ navigationUI: 'hide' })
    ?? node.webkitRequestFullscreen?.()
    ?? node.mozRequestFullScreen?.()
    ?? node.msRequestFullscreen?.(),
  exit: () =>
    document.exitFullscreen?.()
    ?? document.webkitExitFullscreen?.()
    ?? document.mozCancelFullScreen?.()
    ?? document.msExitFullscreen?.(),
  element: () =>
    document.fullscreenElement
    ?? document.webkitFullscreenElement
    ?? document.mozFullScreenElement
    ?? document.msFullscreenElement
    ?? null,
};

export function supported() {
  const node = el();
  return !!(node.requestFullscreen || node.webkitRequestFullscreen
    || node.mozRequestFullScreen || node.msRequestFullscreen);
}

export function isActive() { return raw.element() !== null; }

/**
 * Must be called from inside a user gesture. Rejections are swallowed: a
 * browser refusing fullscreen is not an error worth interrupting play for.
 */
export async function request(lockOrientation = false) {
  if (!supported() || isActive()) return isActive();
  try {
    await raw.request(el());
  } catch (e) {
    return false;
  }
  if (lockOrientation) {
    // Orientation lock only resolves inside fullscreen and only on some
    // engines; a rejection here must not undo the fullscreen we just got.
    try { await screen.orientation?.lock?.('landscape'); } catch (e) { /* not available */ }
  }
  return isActive();
}

export async function exit() {
  if (!isActive()) return;
  try { screen.orientation?.unlock?.(); } catch (e) { /* not available */ }
  try { await raw.exit(); } catch (e) { /* already gone */ }
}

export async function toggle(lockOrientation = false) {
  if (isActive()) { await exit(); return false; }
  return request(lockOrientation);
}

/** @param {(active:boolean)=>void} fn @returns {()=>void} unsubscribe */
export function onChange(fn) {
  const handler = () => fn(isActive());
  const events = ['fullscreenchange', 'webkitfullscreenchange', 'mozfullscreenchange', 'MSFullscreenChange'];
  for (const e of events) document.addEventListener(e, handler);
  return () => { for (const e of events) document.removeEventListener(e, handler); };
}
